import {Injectable} from '@angular/core';
import {PaymentStep, Plan, ProductType, StripeProduct} from '../types/payment.type';
import {AgencyState, IProductDataUserService, ProductsDetailsAgency, StatsProduct} from '../types/product.type';
import {EMPTY, finalize, lastValueFrom, Subscription, switchMap} from 'rxjs';
import {PaymentDataService} from './payment-data.service';
import {PaymentDataManager} from '../common/elf-store/payment-data/payment-data.manager';
import {SettingsApiService} from './api/settings-api.service';
import {StripeHelperService} from './stripe-helper.service';
import {
  getTier,
  getTierIdForSiteSize,
} from '../components/smart/select-plan/select-accessibility-plan/select-plan.helpers';
import {SUBSCRIPTIONS} from 'src/app/common/utils/constants';
import {UserInfoState} from '../common/elf-store/user-info/user-info.type';
import {UserInfoQuery} from '../common/elf-store/user-info/user-info.query';
import {AgencyService} from './agency.service';
import {AgencySubscriptionStatus} from '../types/agency';
import {EventEmitterService} from './event-emitter.service';
import {AutoUnsubscribe} from '../decorators/auto-unsubscribe.decorator';
import {UserPermissionsService} from './user-permissions.service';
import {UserPermissions} from '../common/elf-store/user-permissions/user-permissions.store';
import {
  AgencySiteBillingDetailsDto,
  AgencySiteConfigProductState,
  InstalledA11YSolution,
  ProductStatus,
  SiteSearchEntryDto,
  SubscriptionBillingCycleType,
} from '../../userway/sdk/UserWayApiClient';
import {AmplitudeService} from './amplitude.service';

export const Plans: Array<Plan> = [
  {
    title: 'Pro',
    value: 'pro',
    icon: 'pro-pic',
    desc: 'Basic compliance for small businesses',
    price: 490,
    upTo: 'up to 100k',
    id: ProductType.Remediation,
    btnTitle: 'Upgrade to Pro',
    loading: false,
    benefits: ['<strong>Basic</strong>', '<strong>Unlimited</strong> website pages', 'Monitor 10 pages, with alerts'],
    purchasedProperties: {
      purchaseTriggeredPlace: 'overview-tab',
      purchaseProductName: 'pro-site',
    },
  },
  {
    title: 'Pro Plus',
    value: 'plus',
    icon: 'pro-plus-pic',
    desc: 'Advanced compliance, ideal for medium-sized businesses',
    price: 1190,
    upTo: 'up to 100k',
    id: ProductType.Remediation_Plus,
    btnTitle: 'Upgrade to Pro Plus',
    loading: false,
    benefits: [
      '<strong>Advanced</strong>',
      '<strong>Unlimited</strong> website pages',
      'Usage Stats add-on',
      'Modify Menu add-on',
      'Live Translations add-on',
      'Monitor 50 pages, with alerts',
    ],
    purchasedProperties: {
      purchaseTriggeredPlace: 'overview-tab',
      purchaseProductName: 'pro-plus-site',
    },
  },
  {
    title: 'Ultimate',
    value: 'ultimate',
    icon: 'ultimate-pic',
    desc: 'Full suite of solutions, end-to-end compliance, cross-organization',
    price: 2490,
    upTo: 'up to 100k',
    id: ProductType.Remediation_Ultimate,
    btnTitle: 'Talk to us',
    loading: false,
    benefits: [
      '<strong>Comprehensive</strong>',
      '<strong>Unlimited</strong> website pages',
      'Usage Stats add-on',
      'Modify Menu add-on',
      'Live Translations add-on',
      'Monitor 100 pages, with alerts',
    ],
  },
];

const pricesByTraffic = [
  {pro: 490, plus: 1190, ultimate: 2490, traffic: 100000},
  {pro: 1490, plus: 3620, ultimate: 7570, traffic: 1000000},
  {pro: 3490, plus: 7490, ultimate: 12490, traffic: 2000000},
  {pro: 4540, plus: 14980, ultimate: 24980, traffic: 3000000},
  {pro: 5450, plus: 17980, ultimate: 29980, traffic: 4000000},
  {pro: 5720, plus: 18250, ultimate: 30250, traffic: 5000000},
  {pro: 5990, plus: 18520, ultimate: 30520, traffic: 6000000},
  {pro: 6260, plus: 18790, ultimate: 30790, traffic: 7000000},
  {pro: 6530, plus: 19060, ultimate: 31060, traffic: 8000000},
  {pro: 6800, plus: 19330, ultimate: 31330, traffic: 9000000},
  {pro: 7070, plus: 19600, ultimate: 31600, traffic: 10000000},
  {pro: 7340, plus: 19870, ultimate: 31870, traffic: 11000000},
  {pro: 7610, plus: 20140, ultimate: 32140, traffic: 12000000},
  {pro: 7880, plus: 20410, ultimate: 32410, traffic: 13000000},
  {pro: 8150, plus: 20680, ultimate: 32680, traffic: 14000000},
  {pro: 8420, plus: 20950, ultimate: 32950, traffic: 15000000},
  {pro: 8690, plus: 21220, ultimate: 33220, traffic: 16000000},
  {pro: 8960, plus: 21490, ultimate: 33490, traffic: 17000000},
  {pro: 9230, plus: 21760, ultimate: 33760, traffic: 18000000},
  {pro: 9500, plus: 22030, ultimate: 34030, traffic: 19000000},
  {pro: 9770, plus: 22300, ultimate: 34300, traffic: 20000000},
  {pro: 10040, plus: 22570, ultimate: 34570, traffic: 21000000},
  {pro: 10310, plus: 22840, ultimate: 34840, traffic: 22000000},
  {pro: 10580, plus: 23110, ultimate: 35110, traffic: 23000000},
  {pro: 10850, plus: 23380, ultimate: 35380, traffic: 24000000},
  {pro: 11120, plus: 23650, ultimate: 35650, traffic: 25000000},
  {pro: 11390, plus: 23920, ultimate: 35920, traffic: 26000000},
  {pro: 11660, plus: 24190, ultimate: 36190, traffic: 27000000},
  {pro: 11930, plus: 24460, ultimate: 36460, traffic: 28000000},
  {pro: 12200, plus: 24730, ultimate: 36730, traffic: 29000000},
  {pro: 12470, plus: 25000, ultimate: 37000, traffic: 30000000},
  {pro: 12740, plus: 25270, ultimate: 37270, traffic: 31000000},
  {pro: 13010, plus: 25540, ultimate: 37540, traffic: 32000000},
  {pro: 13280, plus: 25810, ultimate: 37810, traffic: 33000000},
  {pro: 13550, plus: 26080, ultimate: 38080, traffic: 34000000},
  {pro: 13820, plus: 26350, ultimate: 38350, traffic: 35000000},
  {pro: 14090, plus: 26620, ultimate: 38620, traffic: 36000000},
  {pro: 14360, plus: 26890, ultimate: 38890, traffic: 37000000},
  {pro: 14630, plus: 27160, ultimate: 39160, traffic: 38000000},
  {pro: 14900, plus: 27430, ultimate: 39430, traffic: 39000000},
  {pro: 15170, plus: 27700, ultimate: 39700, traffic: 40000000},
  {pro: 15440, plus: 27970, ultimate: 39970, traffic: 41000000},
  {pro: 15710, plus: 28240, ultimate: 40240, traffic: 42000000},
  {pro: 15980, plus: 28510, ultimate: 40510, traffic: 43000000},
  {pro: 16250, plus: 28780, ultimate: 40780, traffic: 44000000},
  {pro: 16520, plus: 29050, ultimate: 41050, traffic: 45000000},
  {pro: 16790, plus: 29320, ultimate: 41320, traffic: 46000000},
  {pro: 17060, plus: 29590, ultimate: 41590, traffic: 47000000},
  {pro: 17330, plus: 29860, ultimate: 41860, traffic: 48000000},
  {pro: 17600, plus: 30130, ultimate: 42130, traffic: 49000000},
  {pro: 17870, plus: 30400, ultimate: 42400, traffic: 50000000},
];

@Injectable({
  providedIn: 'root',
})
@AutoUnsubscribe()
export class SiteOverviewPlanService {
  currentSite: SiteSearchEntryDto;
  BillingCycle = SubscriptionBillingCycleType;
  remediationData: StatsProduct;
  subscriptionData: StatsProduct['subscription'];
  PaymentStep = PaymentStep;
  siteSize: string;
  discountLoading: boolean = false;
  productSubData: IProductDataUserService;
  products: StripeProduct[];
  isAgencyAccount: boolean = false;
  showSwitchToAnnualBtnForAgencyPrepaid: boolean = false;
  baseDiscountPercentBiAnnual: number;
  baseDiscountPercentYearly: number;
  customerPlatformsList: string[] = ['WIX', 'SHOPIFY'];
  getTier: (value: number) => string;
  /* eslint-disable-next-line local/no-subscription-variables */
  private calculatedResponseSubscription: Subscription;
  remediationDataLoading: boolean = false;
  plans = Plans;

  constructor(
    public paymentDataService: PaymentDataService,
    private paymentManager: PaymentDataManager,
    private eventEmitterService: EventEmitterService,
    private userInfoQuery: UserInfoQuery,
    private settingsApiService: SettingsApiService,
    public stripeService: StripeHelperService,
    public agencyService: AgencyService,
    private userPermissionsService: UserPermissionsService,
    private amplitudeService: AmplitudeService,
  ) {
    this.getTier = getTier;
    this.initSubscriptions();
  }

  get loadingPaymentMethodForSubscription() {
    return this.eventEmitterService.loadedPaymentMethodForSubscription.value;
  }

  get currentSubscription() {
    return this.agencyService?.summaryData?.agencySubscriptions.find(
      item => item.status === AgencySubscriptionStatus.ACTIVE || item.status === AgencySubscriptionStatus.NEW,
    );
  }

  initSubscriptions() {
    this[SUBSCRIPTIONS].add(
      this.userInfoQuery.userInfo$.subscribe((info: UserInfoState) => {
        this.isAgencyAccount = info.agencyAccount?.enabled;
      }),
    );

    this[SUBSCRIPTIONS].add(
      this.agencyService.summaryDataTriggerSubject.subscribe({
        next: (loaded: boolean) => {
          if (loaded) {
            this.getAgencySiteBillingDetails();
          }
        },
      }),
    );

    if (this.isAgencyAccount) {
      this.getAgencySiteBillingDetails();
    }
  }

  get isMonthlyBillingCycle(): boolean {
    return this.currentSite.viewInfo?.billingCycle === SubscriptionBillingCycleType.MONTHLY;
  }

  get canAgencySwitchToAnnual(): boolean {
    return !this.isAgencyAccount || this.showSwitchToAnnualBtnForAgencyPrepaid;
  }

  get isManageSubscription(): boolean {
    return !!this.productSubData?.managed_subscription;
  }

  get showSwitchToAnnualButton(): boolean {
    return !this.isManageSubscription && this.isMonthlyBillingCycle && this.canAgencySwitchToAnnual;
  }

  get isPlatformInitialPurchase(): boolean {
    return this.currentSite.isPlatformInitialPurchase;
  }

  initPlanData(site: SiteSearchEntryDto) {
    this.currentSite = site;
    const traffic = site.forcedLoadsPast30Days || site.loadsPast30Days;
    const prices = this.getPricesByTraffic(traffic);
    this.plans = this.plans.map(plan => {
      return {
        ...plan,
        price: prices[plan.value],
        upTo: this.getUpToTraffic(traffic),
        ...(plan.purchasedProperties
          ? {
              purchasedProperties: {
                ...plan.purchasedProperties,
                purchaseTriggeredPage: this.amplitudeService.getCurrentPage(),
              },
            }
          : {}),
      };
    });

    lastValueFrom(
      this.userPermissionsService.checkUserPermissions(UserPermissions.manageBilling.billingOperations).pipe(
        switchMap(isUserHavePermission => {
          if (isUserHavePermission) {
            return this.stripeService.getCustomer();
          } else {
            return EMPTY;
          }
        }),
      ),
    );

    this.getRemediationData();
    this.getPayNowData();
  }

  resetPlanData() {
    this.currentSite = null;
    this.isAgencyAccount = false;
    this.showSwitchToAnnualBtnForAgencyPrepaid = false;
    this.productSubData = null;
    this.eventEmitterService.loadedPaymentMethodForSubscription.next(false);
  }

  getRemediationData() {
    if (!this.currentSite?.id) {
      return;
    }
    this.remediationDataLoading = true;

    this.settingsApiService
      .getProductPaidData(this.currentSite?.id, ProductType.Remediation)
      .pipe(finalize(() => (this.remediationDataLoading = false)))
      .subscribe((response: StatsProduct) => {
        this.remediationData = response;
        this.subscriptionData = response.subscription;
        this.paymentDataService.subscriptionId = response?.subscription?.payment_provider_sub_id;
        if (response.userService) {
          this.productSubData = response.userService;
          this.paymentManager.setIsTrialActive(response.userService.is_trial);

          this.paymentDataService.userServiceId = response.userService.id;
          this.paymentDataService.productSubscriptionId = response.subscription.id;
        } else {
          this.paymentManager.setIsTrialActive(false);
        }
      });
  }

  getPayNowData(): void {
    this.paymentManager.setSites([{id: this.currentSite.id, url: this.currentSite.url}]);
    this.paymentManager.setSelectedProduct(ProductType.Remediation);

    this.paymentDataService.getProducts(false).then((products: StripeProduct[]) => {
      this.products = products;
      this.getDiscountData();
    });
  }

  getDiscountData() {
    this.currentSite?.forcedLoadsPast30Days
      ? (this.siteSize = getTier(this.currentSite.forcedLoadsPast30Days))
      : (this.siteSize = getTier(this.currentSite?.loadsPast30Days));
    if (this.paymentDataService.productPrice) {
      const selectedTier = this.paymentDataService.productPrice.tiers.find(
        tier => tier.id === getTierIdForSiteSize(this.siteSize),
      );
      const {baseDiscountPercentBiAnnual, baseDiscountPercentYearly} = selectedTier;
      this.paymentManager.setBaseDiscountPercentYearly(baseDiscountPercentYearly);
      this.paymentManager.setBaseDiscountPercentBiAnnual(baseDiscountPercentBiAnnual);
      this.baseDiscountPercentBiAnnual = baseDiscountPercentBiAnnual;
      this.baseDiscountPercentYearly = baseDiscountPercentYearly;
      this.discountLoading = false;
    }
  }

  openPayNowForm(): void {
    // activate loader
    this.paymentManager.setIsOnTrialPeriod(true);
    this.paymentDataService.isUpdatePlans = false;
    this.paymentDataService.calculate().finally(() => {
      this.eventEmitterService.loadedPaymentMethodForSubscription.next(false);
    });
  }

  getAgencySiteBillingDetails() {
    if (!this.currentSubscription?.id || !this.currentSite?.id) {
      return;
    }
    const billingSub = this.agencyService
      .getAgencySiteBillingDetails(this.currentSubscription?.id, this.currentSite?.id)
      .subscribe({
        next: (response: AgencySiteBillingDetailsDto) => {
          const product = response.productsMap.REMEDIATION;
          this.showSwitchToAnnualBtnForAgencyPrepaid =
            product.agencyState !== AgencySiteConfigProductState.PAID && product.isEnabled;
          billingSub.unsubscribe();
        },
      });
  }

  switchToAnnual() {
    this.eventEmitterService.loadedPaymentMethodForSubscription.next(true);
    this.paymentDataService
      .switchSiteProductToAnotherBillingCycle({
        productType: ProductType.Remediation,
        site: this.currentSite,
        billingCycle: SubscriptionBillingCycleType.ANNUAL,
      })
      .finally(() => {
        this.eventEmitterService.loadedPaymentMethodForSubscription.next(false);
      });
  }

  isFree(site?: SiteSearchEntryDto): boolean {
    const currentProduct =
      site?.viewInfo?.product || (this.currentSite?.viewInfo?.product as ProductStatus | InstalledA11YSolution);
    return [
      ProductStatus.UW_FREE,
      ProductStatus.EMPTY,
      InstalledA11YSolution.AUDIOEYE_SOLUTION,
      InstalledA11YSolution.EQUALWEB_SOLUTION,
      InstalledA11YSolution.ACCESSIBE_SOLUTION,
    ].includes(currentProduct);
  }

  isPro(): boolean {
    const currentProduct = this.currentSite?.viewInfo?.product as ProductStatus | InstalledA11YSolution;
    return currentProduct === ProductStatus.UW_PRO;
  }

  isProPlus(): boolean {
    const currentProduct = this.currentSite?.viewInfo?.product as ProductStatus | InstalledA11YSolution;
    return currentProduct === ProductStatus.UW_PRO_PLUS;
  }

  isManaged(): boolean {
    const currentProduct = this.currentSite?.viewInfo?.product as ProductStatus | InstalledA11YSolution;
    return currentProduct === ProductStatus.MANAGED;
  }

  isProOrHigher(): boolean {
    return this.isPro() || this.isProPlus() || this.isManaged();
  }

  showPaymentForm(productType: ProductType = ProductType.Remediation): Promise<StripeProduct[]> {
    this.eventEmitterService.loadedPaymentMethodForSubscription.next(true);

    const selectedPlan = this.paymentDataService.billingCycle || SubscriptionBillingCycleType.ANNUAL;
    this.calculatedResponseSubscription?.unsubscribe?.();
    this.calculatedResponseSubscription = this.paymentDataService.calculatedResponseSubscriber.subscribe(value => {
      if (value?.isCompletePaymentState) {
        return;
      }
      this.eventEmitterService.openPaymentSiteDetails.next(PaymentStep.SELECT_PLAN);
    });
    const siteRequestData = {
      id: this.currentSite.id,
      url: this.currentSite.url,
    };
    return this.paymentDataService.setPaymentData(productType, [siteRequestData], selectedPlan);
  }

  getPricesByTraffic(traffic: number) {
    return pricesByTraffic.find(el => el.traffic > traffic) || pricesByTraffic.at(-1);
  }

  private getUpToTraffic(traffic: number) {
    if (traffic < 100000) {
      return 'up to 100K';
    } else {
      return `up to ${Math.floor(traffic / 1e6) + 1 + 'M'}`;
    }
  }
}
